import { shouldTrackAnalytics } from '../config/host';

/**
 * Google Analytics 4 wrapper.
 *
 * All public functions are guarded: if GA has not been initialised
 * (user declined consent, or env var is missing) every call is a silent no-op.
 */

/* ------------------------------------------------------------------ */
/*  gtag type declarations (no @types/gtag.js dependency needed)      */
/* ------------------------------------------------------------------ */

type GtagCommand = 'config' | 'event' | 'js' | 'consent';

interface GtagFn {
  (command: 'js', date: Date): void;
  (command: 'config', targetId: string, config?: Record<string, unknown>): void;
  (
    command: 'event',
    eventName: string,
    eventParams?: Record<string, string | number | boolean>
  ): void;
  (command: 'consent', action: 'default' | 'update', params: Record<string, string>): void;
  // catch-all for future usage
  (command: GtagCommand, ...args: unknown[]): void;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: GtagFn;
  }
}

/* ------------------------------------------------------------------ */
/*  Module state                                                      */
/* ------------------------------------------------------------------ */

let initialised = false;

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/**
 * Dynamically inject the gtag.js script and configure it.
 * Must be called **only after** the user has given analytics consent.
 *
 * No-ops silently when:
 * - `measurementId` is empty/undefined
 * - Already initialised
 * - Running outside a browser (SSR safety)
 */
export function initGA(measurementId: string): void {
  if (initialised) return;
  if (!measurementId) return;
  if (typeof window === 'undefined') return;
  if (!shouldTrackAnalytics()) return;

  // 1. Bootstrap dataLayer + gtag function stub
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(args);
  } as GtagFn;
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // we send page views manually via trackPageView
  });

  // 2. Load the script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  initialised = true;
}

/**
 * Send a `page_view` event. Typically called on every route change.
 */
export function trackPageView(path: string): void {
  if (!initialised) return;
  window.gtag('event', 'page_view', {
    page_path: path,
  });
}

/**
 * Send a custom event (e.g. `search_submit`, `card_swipe`).
 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!initialised) return;
  window.gtag('event', name, params);
}
